import { useState, useEffect, useCallback, useRef } from 'react';
import {
  HiUpload, HiDocumentText, HiPhotograph, HiTable,
  HiArchive, HiDocument, HiDownload, HiTrash, HiLink,
  HiSearch, HiX, HiCheck, HiEye,
} from 'react-icons/hi';
import toast from 'react-hot-toast';
import Layout from '../components/Layout';
import { supabase } from '../supabase/client';
import { useAuth } from '../context/AuthContext';
import { formatDate } from '../utils/format';

// ─── Constants ────────────────────────────────────────────────────────────────

const ALLOWED_TYPES = [
  'image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml',
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'text/csv',
  'application/zip', 'application/x-zip-compressed',
];
const MAX_SIZE   = 10 * 1024 * 1024; // 10 MB
const ACCEPT_STR = '.jpg,.jpeg,.png,.gif,.webp,.svg,.pdf,.doc,.docx,.xls,.xlsx,.csv,.zip';

// ─── File type config ─────────────────────────────────────────────────────────

const getFileType = (mimeType = '') => {
  if (mimeType.startsWith('image/'))                                        return 'image';
  if (mimeType === 'application/pdf')                                       return 'pdf';
  if (mimeType.includes('word') || mimeType.includes('document'))           return 'doc';
  if (mimeType.includes('sheet') || mimeType.includes('excel') ||
      mimeType === 'text/csv')                                              return 'sheet';
  if (mimeType.includes('zip')  || mimeType.includes('compressed'))        return 'zip';
  return 'file';
};

const FILE_CFG = {
  image: { icon: HiPhotograph,   bg: 'bg-blue-50',   color: 'text-blue-500',   label: 'Image'   },
  pdf:   { icon: HiDocumentText, bg: 'bg-red-50',    color: 'text-red-500',    label: 'PDF'     },
  doc:   { icon: HiDocumentText, bg: 'bg-indigo-50', color: 'text-indigo-600', label: 'Document'},
  sheet: { icon: HiTable,        bg: 'bg-green-50',  color: 'text-green-600',  label: 'Sheet'   },
  zip:   { icon: HiArchive,      bg: 'bg-yellow-50', color: 'text-yellow-600', label: 'Archive' },
  file:  { icon: HiDocument,     bg: 'bg-gray-50',   color: 'text-gray-400',   label: 'File'    },
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

const formatSize = (bytes) => {
  if (!bytes) return '—';
  if (bytes < 1024)       return `${bytes} B`;
  if (bytes < 1048576)    return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1048576).toFixed(1)} MB`;
};

// ─── Upload Modal ─────────────────────────────────────────────────────────────

function UploadModal({ file, clients, onClose, onUploaded }) {
  const { user }  = useAuth();
  const [clientId,         setClientId]         = useState('');
  const [shareWithClient,  setShareWithClient]  = useState(false);
  const [uploading,        setUploading]        = useState(false);
  const [progress,         setProgress]         = useState(0);

  useEffect(() => {
    const h = (e) => { if (e.key === 'Escape' && !uploading) onClose(); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [onClose, uploading]);

  const fileType = getFileType(file.type);
  const cfg      = FILE_CFG[fileType];
  const Icon     = cfg.icon;

  const handleUpload = async () => {
    setUploading(true);
    setProgress(0);

    // Smooth fake progress — decelerates naturally so it doesn't hit 100 before upload finishes
    let pct = 0;
    const timer = setInterval(() => {
      pct += pct < 30 ? 12 : pct < 60 ? 6 : pct < 80 ? 2 : 0.4;
      if (pct >= 85) { clearInterval(timer); pct = 85; }
      setProgress(Math.round(pct));
    }, 200);

    try {
      // Build unique storage path
      const ext         = file.name.split('.').pop();
      const unique      = `${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`;
      const storagePath = `${user.id}/${unique}`;

      const { error: uploadErr } = await supabase.storage
        .from('files')
        .upload(storagePath, file, { cacheControl: '3600', upsert: false });

      clearInterval(timer);
      if (uploadErr) throw uploadErr;

      setProgress(95);

      const { error: dbErr } = await supabase.from('files').insert([{
        user_id:            user.id,
        client_id:          clientId || null,
        name:               file.name,
        size:               file.size,
        type:               file.type,
        path:               storagePath,
        shared_with_client: shareWithClient,
      }]);

      if (dbErr) throw dbErr;

      setProgress(100);
      setTimeout(() => {
        toast.success('File uploaded!');
        onUploaded();
      }, 300);
    } catch (err) {
      clearInterval(timer);
      toast.error(err.message ?? 'Upload failed');
      setUploading(false);
      setProgress(0);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm"
      onClick={(e) => { if (e.target === e.currentTarget && !uploading) onClose(); }}
    >
      <div className="bg-white w-full max-w-md rounded-2xl shadow-2xl border border-gray-100">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h2 className="text-base font-semibold text-gray-900">Upload File</h2>
          {!uploading && (
            <button
              onClick={onClose}
              className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition"
            >
              <HiX className="w-5 h-5" />
            </button>
          )}
        </div>

        <div className="px-6 py-5 space-y-4">

          {/* File preview chip */}
          <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl border border-gray-100">
            <div className={`${cfg.bg} p-3 rounded-xl flex-shrink-0`}>
              <Icon className={`w-7 h-7 ${cfg.color}`} />
            </div>
            <div className="min-w-0">
              <p className="text-sm font-semibold text-gray-900 truncate">{file.name}</p>
              <p className="text-xs text-gray-400 mt-0.5">{cfg.label} · {formatSize(file.size)}</p>
            </div>
          </div>

          {/* Client dropdown */}
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
              Assign to Client <span className="font-normal text-gray-400 normal-case tracking-normal">(optional)</span>
            </label>
            <select
              value={clientId}
              onChange={(e) => { setClientId(e.target.value); if (!e.target.value) setShareWithClient(false); }}
              disabled={uploading}
              className="w-full px-3 py-2.5 border border-gray-200 bg-gray-50 rounded-xl text-sm text-gray-900
                focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent
                disabled:opacity-60 transition"
            >
              <option value="">No client</option>
              {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>

          {/* Share toggle */}
          <button
            type="button"
            onClick={() => setShareWithClient((v) => !v)}
            disabled={!clientId || uploading}
            className={`flex items-center justify-between w-full px-4 py-3.5 rounded-xl border transition
              ${!clientId
                ? 'opacity-40 cursor-not-allowed border-gray-100 bg-gray-50'
                : shareWithClient
                  ? 'border-indigo-200 bg-indigo-50'
                  : 'border-gray-200 bg-gray-50 hover:border-indigo-200 hover:bg-indigo-50/30'
              }`}
          >
            <div className="text-left">
              <p className={`text-sm font-semibold ${shareWithClient && clientId ? 'text-indigo-700' : 'text-gray-700'}`}>
                Share with client
              </p>
              <p className="text-xs text-gray-400 mt-0.5">
                {clientId ? 'Client can access this file via their portal' : 'Select a client first'}
              </p>
            </div>
            {/* Toggle pill */}
            <div className={`w-11 h-6 rounded-full transition-colors flex items-center px-0.5 flex-shrink-0 ml-4
              ${shareWithClient && clientId ? 'bg-indigo-600' : 'bg-gray-200'}`}>
              <div className={`w-5 h-5 bg-white rounded-full shadow transition-transform duration-200
                ${shareWithClient && clientId ? 'translate-x-5' : 'translate-x-0'}`} />
            </div>
          </button>

          {/* Progress bar */}
          {uploading && (
            <div>
              <div className="flex justify-between text-xs text-gray-500 mb-1.5">
                <span>Uploading…</span>
                <span className="font-semibold text-indigo-600">{progress}%</span>
              </div>
              <div className="w-full bg-gray-100 rounded-full h-2 overflow-hidden">
                <div
                  className="bg-indigo-600 h-2 rounded-full transition-all duration-300 ease-out"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100">
          <button
            onClick={onClose}
            disabled={uploading}
            className="px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 rounded-xl transition disabled:opacity-40"
          >
            Cancel
          </button>
          <button
            onClick={handleUpload}
            disabled={uploading}
            className="px-5 py-2 text-sm font-semibold bg-indigo-600 hover:bg-indigo-700 text-white
              rounded-xl transition shadow-sm disabled:opacity-60 flex items-center gap-2"
          >
            {uploading
              ? <>
                  <svg className="animate-spin h-3.5 w-3.5" viewBox="0 0 24 24" fill="none">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"/>
                  </svg>
                  Uploading…
                </>
              : <><HiUpload className="w-3.5 h-3.5" /> Upload</>
            }
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Delete Modal ─────────────────────────────────────────────────────────────

function DeleteModal({ file, onClose, onConfirm, deleting }) {
  useEffect(() => {
    const h = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-white w-full max-w-sm rounded-2xl shadow-2xl border border-gray-100 p-6 text-center">
        <div className="w-14 h-14 bg-red-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
          <HiTrash className="w-7 h-7 text-red-500" />
        </div>
        <h3 className="text-base font-bold text-gray-900 mb-2">Delete File</h3>
        <p className="text-sm text-gray-500 leading-relaxed">
          Delete <span className="font-semibold text-gray-800 break-all">{file.name}</span>?
          <br />This removes the file permanently from storage.
        </p>
        <div className="flex gap-3 mt-6">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-600 border border-gray-200 hover:bg-gray-50 rounded-xl transition"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={deleting}
            className="flex-1 px-4 py-2.5 text-sm font-semibold bg-red-500 hover:bg-red-600 text-white rounded-xl transition disabled:opacity-60"
          >
            {deleting ? 'Deleting…' : 'Yes, Delete'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── File Card ────────────────────────────────────────────────────────────────

function FileCard({ file, onDownload, onCopyLink, onToggleShare, onDelete }) {
  const fileType = getFileType(file.type);
  const cfg      = FILE_CFG[fileType];
  const Icon     = cfg.icon;

  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 flex flex-col gap-3
      hover:shadow-md transition-shadow">

      {/* Type icon + share badge */}
      <div className="flex items-start justify-between">
        <div className={`${cfg.bg} p-3 rounded-xl`}>
          <Icon className={`w-7 h-7 ${cfg.color}`} />
        </div>
        <span className={`text-xs font-semibold px-2.5 py-1 rounded-full flex-shrink-0 ${
          file.shared_with_client
            ? 'bg-green-100 text-green-700'
            : 'bg-gray-100 text-gray-500'
        }`}>
          {file.shared_with_client ? 'Shared' : 'Private'}
        </span>
      </div>

      {/* Name + meta */}
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-gray-900 leading-snug line-clamp-2 break-all">{file.name}</p>
        <p className="text-xs text-gray-400 mt-1">{cfg.label} · {formatSize(file.size)}</p>
      </div>

      {/* Client */}
      <p className="text-xs truncate">
        {file.clients?.name
          ? <span className="text-indigo-600 font-medium">{file.clients.name}</span>
          : <span className="text-gray-300 italic">No client assigned</span>
        }
      </p>

      {/* Date */}
      <p className="text-xs text-gray-400">{formatDate(file.created_at)}</p>

      {/* Action buttons */}
      <div className="flex items-center gap-0.5 pt-2 border-t border-gray-50 -mx-1">
        <button
          onClick={() => onDownload(file)}
          title="Download"
          className="flex-1 flex items-center justify-center gap-1 py-2 text-xs font-medium
            text-gray-500 hover:text-indigo-600 hover:bg-indigo-50 rounded-xl transition"
        >
          <HiDownload className="w-3.5 h-3.5" />
          <span>Download</span>
        </button>
        <button
          onClick={() => onCopyLink(file)}
          title="Copy 7-day share link"
          className="flex-1 flex items-center justify-center gap-1 py-2 text-xs font-medium
            text-gray-500 hover:text-indigo-600 hover:bg-indigo-50 rounded-xl transition"
        >
          <HiLink className="w-3.5 h-3.5" />
          <span>Copy Link</span>
        </button>
        {/* Share toggle */}
        <button
          onClick={() => onToggleShare(file)}
          title={file.shared_with_client ? 'Set to private' : 'Share with client'}
          className={`p-2 rounded-xl transition ${
            file.shared_with_client
              ? 'text-green-600 hover:text-red-500 hover:bg-red-50'
              : 'text-gray-400 hover:text-green-600 hover:bg-green-50'
          }`}
        >
          {file.shared_with_client
            ? <HiCheck className="w-4 h-4" />
            : <HiEye className="w-4 h-4" />
          }
        </button>
        {/* Delete */}
        <button
          onClick={() => onDelete(file)}
          title="Delete file"
          className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-xl transition"
        >
          <HiTrash className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

// ─── Drop Zone ────────────────────────────────────────────────────────────────

function DropZone({ onFileSelect, dragActive, onDragEnter, onDragLeave, onDragOver, onDrop }) {
  const inputRef = useRef(null);

  return (
    <div
      onDragEnter={onDragEnter}
      onDragLeave={onDragLeave}
      onDragOver={onDragOver}
      onDrop={onDrop}
      onClick={() => inputRef.current?.click()}
      className={`border-2 border-dashed rounded-2xl p-8 text-center cursor-pointer transition-all duration-200
        ${dragActive
          ? 'border-indigo-400 bg-indigo-50 scale-[1.01]'
          : 'border-gray-200 bg-white hover:border-indigo-300 hover:bg-indigo-50/30'
        }`}
    >
      <input
        ref={inputRef}
        type="file"
        accept={ACCEPT_STR}
        hidden
        onChange={(e) => {
          if (e.target.files[0]) onFileSelect(e.target.files[0]);
          e.target.value = '';
        }}
      />
      <div className={`w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-3 transition-colors
        ${dragActive ? 'bg-indigo-100' : 'bg-gray-50 border border-gray-100'}`}>
        <HiUpload className={`w-7 h-7 transition-colors ${dragActive ? 'text-indigo-600' : 'text-gray-400'}`} />
      </div>
      <p className={`text-sm font-semibold transition-colors ${dragActive ? 'text-indigo-700' : 'text-gray-700'}`}>
        {dragActive ? 'Drop to upload' : 'Drag & drop or click to upload'}
      </p>
      <p className="text-xs text-gray-400 mt-1">
        PDF, images, Word, Excel, ZIP · Max 10 MB per file
      </p>
    </div>
  );
}

// ─── Skeleton grid card ───────────────────────────────────────────────────────

function SkeletonCard() {
  return (
    <div className="bg-white rounded-2xl border border-gray-100 p-5 animate-pulse flex flex-col gap-3">
      <div className="flex items-start justify-between">
        <div className="w-14 h-14 bg-gray-200 rounded-xl" />
        <div className="h-5 bg-gray-100 rounded-full w-14" />
      </div>
      <div className="space-y-2">
        <div className="h-4 bg-gray-200 rounded-full w-3/4" />
        <div className="h-3 bg-gray-100 rounded-full w-1/2" />
      </div>
      <div className="h-3 bg-gray-100 rounded-full w-1/3" />
      <div className="h-3 bg-gray-100 rounded-full w-1/4" />
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function Files() {
  const { user } = useAuth();

  const [files,        setFiles]        = useState([]);
  const [clients,      setClients]      = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [search,       setSearch]       = useState('');
  const [clientFilter, setClientFilter] = useState('all');
  const [dragActive,   setDragActive]   = useState(false);
  const [pendingFile,  setPendingFile]  = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting,     setDeleting]     = useState(false);

  // Track drag count to avoid false onDragLeave from child elements
  const dragCount = useRef(0);

  // ── Fetch ──────────────────────────────────────────────────────────────────

  const fetchData = useCallback(async () => {
    setLoading(true);
    const [filesRes, clientsRes] = await Promise.all([
      supabase
        .from('files')
        .select('*, clients(name)')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false }),
      supabase
        .from('clients')
        .select('id, name')
        .eq('user_id', user.id)
        .eq('status', 'Active')
        .order('name'),
    ]);
    if (filesRes.error)   toast.error('Failed to load files');
    if (clientsRes.error) toast.error('Failed to load clients');
    setFiles(filesRes.data   ?? []);
    setClients(clientsRes.data ?? []);
    setLoading(false);
  }, [user.id]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // ── Validate + open upload modal ───────────────────────────────────────────

  const handleFileSelect = (file) => {
    if (!ALLOWED_TYPES.includes(file.type)) {
      toast.error('Unsupported file type. Upload PDF, image, Word, Excel, or ZIP.');
      return;
    }
    if (file.size > MAX_SIZE) {
      toast.error(`File too large (${formatSize(file.size)}). Max 10 MB.`);
      return;
    }
    setPendingFile(file);
  };

  // ── Drag events (counter prevents flicker on child elements) ───────────────

  const onDragEnter = (e) => { e.preventDefault(); dragCount.current++; setDragActive(true); };
  const onDragLeave = (e) => {
    e.preventDefault();
    dragCount.current--;
    if (dragCount.current === 0) setDragActive(false);
  };
  const onDragOver = (e) => { e.preventDefault(); };
  const onDrop     = (e) => {
    e.preventDefault();
    dragCount.current = 0;
    setDragActive(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFileSelect(file);
  };

  // ── Download (1-hour signed URL) ───────────────────────────────────────────

  const handleDownload = async (file) => {
    const { data, error } = await supabase.storage
      .from('files')
      .createSignedUrl(file.path, 3600);
    if (error) { toast.error('Could not generate download link'); return; }
    const a = document.createElement('a');
    a.href = data.signedUrl;
    a.download = file.name;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  // ── Copy share link (7-day signed URL) ────────────────────────────────────

  const handleCopyLink = async (file) => {
    const { data, error } = await supabase.storage
      .from('files')
      .createSignedUrl(file.path, 604800); // 7 days
    if (error) { toast.error('Could not generate share link'); return; }
    try {
      await navigator.clipboard.writeText(data.signedUrl);
      toast.success('Share link copied! Valid for 7 days.');
    } catch {
      toast.error('Could not copy — try again');
    }
  };

  // ── Toggle shared_with_client ──────────────────────────────────────────────

  const handleToggleShare = async (file) => {
    const next = !file.shared_with_client;
    const { error } = await supabase
      .from('files')
      .update({ shared_with_client: next })
      .eq('id', file.id)
      .eq('user_id', user.id);
    if (error) { toast.error(error.message); return; }
    setFiles((prev) =>
      prev.map((f) => f.id === file.id ? { ...f, shared_with_client: next } : f)
    );
    toast.success(next ? 'File is now shared with client' : 'File set to private');
  };

  // ── Delete from storage + DB ───────────────────────────────────────────────

  const handleDelete = async () => {
    setDeleting(true);

    const { error: storageErr } = await supabase.storage
      .from('files')
      .remove([deleteTarget.path]);
    if (storageErr) { toast.error(storageErr.message); setDeleting(false); return; }

    const { error: dbErr } = await supabase
      .from('files')
      .delete()
      .eq('id', deleteTarget.id)
      .eq('user_id', user.id);
    if (dbErr) { toast.error(dbErr.message); setDeleting(false); return; }

    setFiles((prev) => prev.filter((f) => f.id !== deleteTarget.id));
    toast.success('File deleted');
    setDeleteTarget(null);
    setDeleting(false);
  };

  // ── Filter logic ───────────────────────────────────────────────────────────

  const q = search.toLowerCase();
  const filtered = files.filter((f) => {
    const matchSearch = f.name.toLowerCase().includes(q);
    const matchClient =
      clientFilter === 'all'
        ? true
        : clientFilter === 'none'
          ? f.client_id == null
          : f.client_id === clientFilter;
    return matchSearch && matchClient;
  });

  const sharedCount  = files.filter((f) => f.shared_with_client).length;
  const privateCount = files.length - sharedCount;

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <Layout title="Files">

      {/* Page header */}
      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-2 mb-6">
        <div>
          <h2 className="text-xl font-bold text-gray-900">File Sharing</h2>
          <p className="text-sm text-gray-500 mt-0.5">
            {loading
              ? 'Loading…'
              : `${files.length} file${files.length !== 1 ? 's' : ''} · ${sharedCount} shared · ${privateCount} private`
            }
          </p>
        </div>
      </div>

      {/* Drop zone */}
      <div className="mb-6">
        <DropZone
          onFileSelect={handleFileSelect}
          dragActive={dragActive}
          onDragEnter={onDragEnter}
          onDragLeave={onDragLeave}
          onDragOver={onDragOver}
          onDrop={onDrop}
        />
      </div>

      {/* Filter bar */}
      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        {/* Search */}
        <div className="relative flex-1">
          <HiSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search files by name…"
            className="w-full pl-10 pr-4 py-2.5 bg-white border border-gray-200 rounded-xl text-sm
              text-gray-900 placeholder-gray-400 shadow-sm focus:outline-none focus:ring-2
              focus:ring-indigo-500 focus:border-transparent"
          />
          {search && (
            <button
              onClick={() => setSearch('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              <HiX className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Client filter */}
        <select
          value={clientFilter}
          onChange={(e) => setClientFilter(e.target.value)}
          className="px-3 py-2.5 bg-white border border-gray-200 rounded-xl text-sm text-gray-700
            shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
        >
          <option value="all">All clients</option>
          <option value="none">No client</option>
          {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
      </div>

      {/* File grid */}
      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
          {[...Array(8)].map((_, i) => <SkeletonCard key={i} />)}
        </div>

      ) : filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <div className="w-16 h-16 bg-indigo-50 rounded-2xl flex items-center justify-center mb-4">
            <HiDocument className="w-8 h-8 text-indigo-400" />
          </div>
          {search || clientFilter !== 'all' ? (
            <>
              <p className="text-sm font-semibold text-gray-700">No files match your filter</p>
              <p className="text-xs text-gray-400 mt-1">Try a different search or client</p>
              <button
                onClick={() => { setSearch(''); setClientFilter('all'); }}
                className="mt-3 text-xs text-indigo-600 font-medium hover:underline"
              >
                Clear filters
              </button>
            </>
          ) : (
            <>
              <p className="text-sm font-semibold text-gray-700">No files uploaded yet</p>
              <p className="text-xs text-gray-400 mt-1">Drag & drop a file above to get started</p>
            </>
          )}
        </div>

      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
            {filtered.map((file) => (
              <FileCard
                key={file.id}
                file={file}
                onDownload={handleDownload}
                onCopyLink={handleCopyLink}
                onToggleShare={handleToggleShare}
                onDelete={setDeleteTarget}
              />
            ))}
          </div>
          {(search || clientFilter !== 'all') && (
            <p className="text-xs text-gray-400 mt-4 px-1">
              Showing {filtered.length} of {files.length} files
            </p>
          )}
        </>
      )}

      {/* Modals */}
      {pendingFile && (
        <UploadModal
          file={pendingFile}
          clients={clients}
          onClose={() => setPendingFile(null)}
          onUploaded={() => { setPendingFile(null); fetchData(); }}
        />
      )}
      {deleteTarget && (
        <DeleteModal
          file={deleteTarget}
          onClose={() => setDeleteTarget(null)}
          onConfirm={handleDelete}
          deleting={deleting}
        />
      )}
    </Layout>
  );
}
